import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { type FC, useState } from "react";

import { Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import type { EvalScoringDimension } from "@api/rest/types-and-hooks";
import { useDeleteEvalCriterion, useGetEvalScoringStandard } from "@api/rest/types-and-hooks";
import { StandardSource } from "@components/EvalReview/constants";
import { useRefreshRubric } from "@components/EvalRubric/useRefreshRubric";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

/** The 1-5 scale, as strings because that is how the API keys them. */
const LEVELS = ["1", "2", "3", "4", "5"] as const;

/**
 * The levels this criterion actually describes, in order.
 *
 * Falls back to nothing when `level_anchors` is empty, in which case the
 * caller shows the joined line — a rubric authored before per-level wording
 * existed has only that.
 */
const describedLevels = (row: EvalScoringDimension): [string, string][] => {
  const levels = (row.level_anchors ?? {}) as Record<string, string>;
  return LEVELS.filter((level) => levels[level]?.trim()).map((level) => [level, levels[level].trim()]);
};

interface StandardInForceProps {
  onEdit: (row: EvalScoringDimension) => void;
}

/**
 * Every scored criterion, in force right now.
 *
 * Flat on purpose. These rows *are* the criteria — there is no nesting, and
 * adding one puts it alongside the others rather than underneath one. Keeping
 * "dimensions" with "criteria" beneath them was my invention and it was the
 * whole confusion.
 *
 * Reads the server's standard rather than restating the defaults here, so the
 * two cannot drift.
 */
export const StandardInForce: FC<StandardInForceProps> = ({ onEdit }) => {
  const { data } = useGetEvalScoringStandard();
  const refreshRubric = useRefreshRubric();
  const remove = useDeleteEvalCriterion();
  const [confirming, setConfirming] = useState<EvalScoringDimension | null>(null);
  const standard = data?.status === 200 ? data.data : undefined;
  if (!standard) return null;

  const deleteRow = async (row: EvalScoringDimension) => {
    if (row.criterion_id == null) return;
    try {
      const result = await remove.mutateAsync({ criterionId: row.criterion_id });
      // The Orval fetcher resolves on 4xx rather than throwing.
      if (result.status === 200) {
        notifySuccess(`Removed ${row.label}.`);
        await refreshRubric();
      } else {
        notifyError((result.data as { detail?: string } | undefined)?.detail ?? "Could not remove it.");
      }
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Could not remove it.");
    } finally {
      setConfirming(null);
    }
  };

  return (
    <section className="rounded-lg border border-border-subtle bg-bg-surface p-4">
      <div className="flex items-baseline justify-between gap-x-4">
        <Label size="small" colorType="secondary" className="uppercase tracking-wide">
          Criteria
        </Label>
        {/* "Defaults" rather than a version, because until someone edits a
            criterion there is no version to name. */}
        <span className="text-xs text-text-tertiary">
          {standard.source === StandardSource.RUBRIC ? `Version ${standard.version}` : "Defaults"}
        </span>
      </div>

      <dl className="mt-3 flex flex-col divide-y divide-border-subtle">
        {standard.dimensions.map((row) => (
          <div key={row.dimension} className="grid grid-cols-[minmax(0,11rem)_1fr_auto] items-start gap-x-4 py-2.5">
            <dt className="flex items-baseline gap-x-2 text-sm text-text-primary">
              {row.label}
              {/* Marked so it is clear which rows came with the product and
                  which someone added. */}
              {!row.is_builtin ? (
                <span className="rounded bg-bg-default px-2 py-0.5 text-xs text-text-secondary">added</span>
              ) : null}
            </dt>
            {/* One level per line. The joined form — "1 = …, 2 = …, 3 = …" —
                was readable when only 1, 3 and 5 were pinned; with all five
                described it is a paragraph you have to parse to find the
                level you want. The numeral leads so the column scans. */}
            <dd>
              {describedLevels(row).length > 0 ? (
                <dl className="flex flex-col gap-y-1">
                  {describedLevels(row).map(([level, text]) => (
                    <div key={level} className="flex items-baseline gap-x-2">
                      <dt className="w-4 shrink-0 text-right text-xs font-medium tabular-nums text-text-tertiary">
                        {level}
                      </dt>
                      <dd className="text-sm leading-snug text-text-secondary">{text}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <span className="text-sm text-text-tertiary">{row.scale_anchors || "—"}</span>
              )}
            </dd>
            <div className="flex items-center gap-x-1">
              <button
                type="button"
                aria-label={`Edit what 1 to 5 means for ${row.label}`}
                onClick={() => onEdit(row)}
                className="cursor-pointer rounded p-1 text-text-tertiary hover:bg-bg-default hover:text-text-secondary"
              >
                <PencilSquareIcon className="size-4" />
              </button>
              {/* Only on rows someone added. A default cannot be removed —
                  it would stop something being scored that always has been —
                  and without this an added one could never be taken back. */}
              {!row.is_builtin ? (
                <button
                  type="button"
                  aria-label={`Remove ${row.label}`}
                  onClick={() => setConfirming(row)}
                  className="cursor-pointer rounded p-1 text-text-tertiary hover:bg-red-50 hover:text-red-600"
                >
                  <TrashIcon className="size-4" />
                </button>
              ) : (
                <span className="size-6" />
              )}
            </div>
          </div>
        ))}
      </dl>

      <Paragraph displayType="normal" size="small" className="mt-3 text-text-tertiary">
        Everything here is scored, by you and by the AI reviewer. The defaults can be reworded but not removed; criteria
        you add can be.
      </Paragraph>

      {confirming ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <span className="text-sm text-text-primary">
            Remove {confirming.label}? Scores already recorded against it are kept.
          </span>
          <div className="ml-auto flex gap-x-2">
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="rounded-md border border-border-medium bg-bg-surface px-2.5 py-1 text-sm text-text-primary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => deleteRow(confirming)}
              className="rounded-md bg-red-600 px-2.5 py-1 text-sm text-white hover:bg-red-700 disabled:opacity-50"
            >
              {remove.isPending ? "Removing…" : "Remove"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};
